import { DateUtils } from '@pbk-web/utils';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Tooltip from '../tooltip/Tooltip';

type TimeAgoProps = {
  date: Date | null;
  mode?: 'ago' | 'till';
  disableTime?: boolean;
  displayText?: string;
  /**
   * `'time-ago'` (default) — fine-grained relative label (seconds → minutes → hours → days →
   * months → years) using `common:time-ago.*` / `common:time-till.*` keys, with auto-refresh.
   *
   * `'date-bucket'` — coarse bucket label (today / N days ago / N months ago / N years ago)
   * using `common:date.*` keys. No auto-refresh. Tooltip shows the contextual display date
   * (weekday + time when within the current week, full datetime otherwise).
   */
  variant?: 'time-ago' | 'date-bucket';
};

const TimeAgo: FC<TimeAgoProps> = (props) => {
  const { date, mode = 'ago', disableTime, variant = 'time-ago', displayText } = props;
  const { t } = useTranslation(['common']);
  const updateTimer = useRef<number>(0);
  const [text, setText] = useState('-');

  const updateText = useCallback(
    (date: Date | null) => {
      // Drop a refresh scheduled for a previous date, or it overwrites the new label.
      clearTimeout(updateTimer.current);

      if (!date) {
        setText('-');
        return;
      }

      if (variant === 'date-bucket') {
        const daysAgo = Math.abs(DateUtils.getDaysBetween(new Date(), date));
        const monthsAgo = Math.floor(daysAgo / 30);
        const yearsAgo = Math.floor(daysAgo / 365);

        if (yearsAgo >= 1) {
          setText(t('common:date.years-ago', { count: yearsAgo, yearsAgo }));
        } else if (monthsAgo >= 1) {
          setText(t('common:date.months-ago', { count: monthsAgo, monthsAgo }));
        } else if (daysAgo >= 1) {
          setText(t('common:date.days-ago', { count: daysAgo, daysAgo }));
        } else {
          setText(t('common:date.today'));
        }
        // No auto-refresh for date-bucket — day-level granularity doesn't need it
        return;
      }

      const diffrence = mode === 'ago' ? new Date().getTime() - date.getTime() : date.getTime() - new Date().getTime();
      const seconds = Math.floor(diffrence / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(seconds / 3600);
      const days = Math.floor(hours / 24);
      const months = Math.floor(days / 30);
      const years = Math.floor(days / 365);

      let time = disableTime ? DateUtils.formatDate(date) : DateUtils.formatDateTime(date);
      if (years >= 1) {
        time = t(mode === 'ago' ? 'common:time-ago.years' : 'common:time-till.years', { count: years, years });
      } else if (months >= 1) {
        time = t(mode === 'ago' ? 'common:time-ago.months' : 'common:time-till.months', { count: months, months });
      } else if (days >= 1) {
        time = t(mode === 'ago' ? 'common:time-ago.days' : 'common:time-till.days', { count: days, days });
      } else if (hours >= 1) {
        time = t(mode === 'ago' ? 'common:time-ago.hours' : 'common:time-till.hours', { count: hours, hours });
      } else if (minutes >= 1) {
        time = t(mode === 'ago' ? 'common:time-ago.minutes' : 'common:time-till.minutes', { count: minutes, minutes });
      } else if (seconds >= 1) {
        time = t(mode === 'ago' ? 'common:time-ago.seconds' : 'common:time-till.seconds', { count: seconds, seconds });
      }
      if (seconds < 5) {
        time = t(mode === 'ago' ? 'common:time-ago.a-few-seconds' : 'common:time-till.a-few-seconds');
      }

      setText(time);

      // update time after a bit
      if (seconds < 60) {
        updateTimer.current = window.setTimeout(() => updateText(date), 5 * 1000 /* 5 sec */);
      } else if (hours < 1) {
        updateTimer.current = window.setTimeout(() => updateText(date), 5 * 60 * 1000 /* 5 min */);
      }
    },
    [disableTime, mode, t, variant],
  );

  useEffect(() => {
    return () => clearTimeout(updateTimer.current);
  }, []);

  useEffect(() => {
    updateText(date);
  }, [date, updateText]);

  const tooltipText =
    variant === 'date-bucket'
      ? date && DateUtils.getDisplayDate(date, t)
      : date && (disableTime ? DateUtils.formatDate(date) : DateUtils.formatDateTime(date));

  return (
    <Tooltip text={tooltipText}>
      {(tooltip) => (
        <span {...tooltip} className="relative">
          {displayText ? displayText.replace('{time}', text) : text}
        </span>
      )}
    </Tooltip>
  );
};

export default TimeAgo;
